import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAppContext } from './AppContext';
import { resolveAuthorName, filterNotes, sortNotesDesc, noteInsertRow } from '../utils';

var h = React.createElement;

// Sidebar is 196px wide — save bar must clear it
var SIDEBAR_W = 196;

function fmtNoteDate(iso) {
  if (!iso) return '';
  var d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default function ProjectNotesTab() {
  var ctx = useAppContext();
  var projects     = ctx.projects     || [];
  var userEmail    = ctx.userEmail    || '';
  var firmSettings = ctx.firmSettings || {};
  var studioTeam   = firmSettings.studioTeam || [];

  var ss  = useState(null);  var selProject = ss[0];  var setSelProject  = ss[1];
  var ns  = useState([]);    var notes      = ns[0];  var setNotes       = ns[1];
  var qs  = useState('');    var search     = qs[0];  var setSearch      = qs[1];
  var ds  = useState('');    var draft      = ds[0];  var setDraft       = ds[1];
  var ls  = useState(false); var loading    = ls[0];  var setLoading     = ls[1];
  var svs = useState(false); var saving     = svs[0]; var setSaving      = svs[1];
  var eis = useState(null);  var editingId  = eis[0]; var setEditingId   = eis[1];
  var ebs = useState('');    var editBody   = ebs[0]; var setEditBody    = ebs[1];
  var lss = useState(null);  var lastSaved  = lss[0]; var setLastSaved   = lss[1];

  function loadNotes(code) {
    if (!code) return;
    setLoading(true);
    supabase.from('notes').select('*')
      .eq('project_code', code)
      .eq('entity_type', 'project')
      .then(function(r) {
        setLoading(false);
        if (!r.error) setNotes(r.data || []);
      });
  }

  useEffect(function() { loadNotes(selProject); }, [selProject]);

  function handleSave() {
    if (!draft.trim() || !selProject || saving) return;
    setSaving(true);
    supabase.from('notes').insert(noteInsertRow(draft, selProject, 'project', null, userEmail)).select()
      .then(function(r) {
        setSaving(false);
        if (!r.error && r.data && r.data[0]) {
          setNotes(function(prev) { return [r.data[0]].concat(prev); });
          setDraft('');
          setLastSaved(new Date().toISOString());
        }
      });
  }

  function handleUpdate(id) {
    if (!editBody.trim()) return;
    supabase.from('notes')
      .update({ body: editBody.trim(), updated_at: new Date().toISOString() })
      .eq('id', id).select()
      .then(function(r) {
        if (!r.error && r.data && r.data[0]) {
          setNotes(function(prev) { return prev.map(function(n) { return n.id === id ? r.data[0] : n; }); });
          setEditingId(null);
          setEditBody('');
        }
      });
  }

  var displayed = sortNotesDesc(filterNotes(notes, search));
  var canSave   = !!draft.trim() && !!selProject && !saving;

  return h('div', { style: { paddingBottom: 80 } },

    // ── Header ──────────────────────────────────────────────────────────────
    h('div', { style: { fontSize: 20, fontWeight: 700, letterSpacing: -0.4, marginBottom: 20 } }, 'Project Notes'),

    // ── Project ribbon ───────────────────────────────────────────────────────
    h('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 } },
      projects.map(function(proj) {
        var code   = proj.label || proj.id;
        var active = selProject === code;
        return h('button', {
          key: code,
          onClick: function() { setSelProject(code); setSearch(''); setEditingId(null); },
          style: {
            padding: '6px 16px', borderRadius: 20, fontSize: 12,
            fontWeight: active ? 600 : 400,
            border: active ? '1.5px solid #3730a3' : '1px solid #ddd',
            background: active ? '#ededff' : '#fff',
            color:  active ? '#3730a3' : '#555',
            cursor: 'pointer', transition: 'all .1s',
          },
        }, proj.displayName || code);
      })
    ),

    // ── No project selected ──────────────────────────────────────────────────
    !selProject
      ? h('div', { style: { padding: '60px 0', textAlign: 'center', color: '#bbb', fontSize: 14 } },
          'Select a project above to view and add notes.')

      : h('div', null,

          // ── Search ────────────────────────────────────────────────────────
          h('div', { style: { marginBottom: 16, position: 'relative' } },
            h('span', { style: { position: 'absolute', left: 10, top: 9, fontSize: 14, color: '#bbb', pointerEvents: 'none', lineHeight: 1 } }, '🔍'),
            h('input', {
              type: 'text', value: search,
              onChange: function(e) { setSearch(e.target.value); },
              placeholder: 'Search notes\u2026',
              style: {
                width: '100%', boxSizing: 'border-box', height: 36,
                border: '1px solid #ddd', borderRadius: 6,
                padding: '0 12px 0 34px', fontSize: 13, outline: 'none',
                fontFamily: 'inherit',
              },
            })
          ),

          // ── New note textarea ─────────────────────────────────────────────
          h('textarea', {
            value: draft,
            onChange: function(e) { setDraft(e.target.value); },
            placeholder: 'Add a project note\u2026',
            rows: 4,
            style: {
              width: '100%', boxSizing: 'border-box',
              border: '1px solid #ddd', borderRadius: 6,
              padding: '10px 12px', fontSize: 13, fontFamily: 'inherit',
              resize: 'vertical', outline: 'none', marginBottom: 16,
            },
          }),

          // ── Feed ──────────────────────────────────────────────────────────
          loading
            ? h('div', { style: { color: '#bbb', fontSize: 13, padding: '20px 0' } }, 'Loading\u2026')

            : displayed.length === 0
              ? h('div', { style: { color: '#bbb', fontSize: 13, padding: '20px 0' } },
                  search ? 'No notes match your search.' : 'No notes yet — add the first one above.')

              : h('div', { style: { display: 'flex', flexDirection: 'column', gap: 10 } },
                  displayed.map(function(note) {
                    var isEditing  = editingId === note.id;
                    var authorName = resolveAuthorName(note.author_email, studioTeam);
                    var wasEdited  = note.updated_at && note.updated_at !== note.created_at;

                    return h('div', {
                      key: note.id,
                      style: {
                        border: '1px solid #e8e8e8', borderRadius: 8,
                        padding: '12px 14px', background: '#fff',
                      },
                    },
                      // Meta row
                      h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 } },
                        h('span', { style: { fontSize: 12, fontWeight: 600, color: '#333' } }, authorName),
                        h('span', { style: { fontSize: 11, color: '#bbb' } },
                          fmtNoteDate(note.created_at),
                          wasEdited ? h('span', { style: { marginLeft: 6, fontStyle: 'italic' } }, '(edited)') : null
                        )
                      ),

                      // Body / edit mode
                      isEditing
                        ? h('div', null,
                            h('textarea', {
                              value: editBody,
                              onChange: function(e) { setEditBody(e.target.value); },
                              rows: 3, autoFocus: true,
                              style: {
                                width: '100%', boxSizing: 'border-box',
                                border: '1px solid #a5b4fc', borderRadius: 5,
                                padding: '8px 10px', fontSize: 13, fontFamily: 'inherit',
                                resize: 'vertical', outline: 'none', marginBottom: 8,
                              },
                            }),
                            h('div', { style: { display: 'flex', gap: 8 } },
                              h('button', {
                                onClick: function() { handleUpdate(note.id); },
                                style: { padding: '4px 12px', background: '#111', color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' },
                              }, 'Save'),
                              h('button', {
                                onClick: function() { setEditingId(null); setEditBody(''); },
                                style: { padding: '4px 12px', background: 'none', color: '#888', border: '1px solid #ddd', borderRadius: 5, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' },
                              }, 'Cancel')
                            )
                          )
                        : h('div', null,
                            h('div', { style: { fontSize: 13, color: '#222', lineHeight: 1.55, whiteSpace: 'pre-wrap' } }, note.body),
                            h('button', {
                              onClick: function() { setEditingId(note.id); setEditBody(note.body); },
                              style: { marginTop: 7, fontSize: 11, color: '#bbb', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' },
                            }, 'Edit')
                          )
                    );
                  })
                )
        ),

    // ── Fixed save bar ───────────────────────────────────────────────────────
    h('div', {
      style: {
        position: 'fixed', bottom: 0, left: SIDEBAR_W, right: 0,
        background: '#fff', borderTop: '1px solid #ebebeb',
        padding: '10px 28px', display: 'flex', alignItems: 'center', gap: 16,
        zIndex: 100,
      },
    },
      h('button', {
        onClick: handleSave,
        disabled: !canSave,
        style: {
          height: 32, padding: '0 18px',
          background: canSave ? '#111' : '#eee',
          color:      canSave ? '#fff' : '#aaa',
          border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 500,
          cursor: canSave ? 'pointer' : 'default', fontFamily: 'inherit',
        },
      }, saving ? 'Saving\u2026' : 'Save Note'),
      lastSaved
        ? h('span', { style: { fontSize: 11, color: '#bbb' } }, 'Saved ' + fmtNoteDate(lastSaved))
        : null
    )
  );
}
